/* Cookie consent banner. Whether disclosure is required at all is decided
   server-side (see templates/_includes/_cookie-banner.twig) via Servd's
   CF-IPCountry header; this only handles the disclosure itself: persisting
   the visitor's choice, honoring a global opt-out signal, and activating any
   gated ("dormant") scripts once they say yes to analytics. */
export function cookieConsent(keyVersion, consentRequired = true) {

  const itemKey = `cookieConsent:${keyVersion}`;

  return {
    consent: null, // null | "all" | "required"

    get visible() {
      return this.consent === null;
    },

    init() {
      const stored = localStorage.getItem(itemKey);
      const initial =
        stored === "all" || stored === "required"
          ? stored
          : this.prefersPrivacy()
            ? // Global Privacy Control (a recognized opt-out signal under
              // CPRA and Colorado's privacy law) or legacy Do Not Track
              // always wins, regardless of geography - no banner, no
              // tracking.
              "required"
            : consentRequired
              ? null
              : "all";

      this.apply(initial);
    },

    acceptAll() {
      this.apply("all");
    },

    allowRequiredOnly() {
      this.apply("required");
    },

    // Reachable from anywhere on the page via:
    // <button x-data @click="$dispatch('open-cookie-preferences', { bubbles: true })">
    reopen() {
      this.apply(null);
    },

    apply(value) {
      this.consent = value;
      window.cookieConsent = value;

      if (value === "all" || value === "required") {
        localStorage.setItem(itemKey, value);
      } else {
        localStorage.removeItem(itemKey);
      }

      if (value === "all") {
        this.activateConsentScripts("analytics");
      }

      // Announced last, once any gated scripts are live, so a listener can
      // count on them being there (see parts/analytics.js).
      window.dispatchEvent(new CustomEvent("cookie-consent", { detail: value }));
    },

    prefersPrivacy() {
      return (
        navigator.globalPrivacyControl === true ||
        navigator.doNotTrack === "1" ||
        window.doNotTrack === "1" ||
        navigator.msDoNotTrack === "1"
      );
    },

    activateConsentScripts(category) {
      const selector = `script[type="text/plain"][data-consent="${category}"]:not([data-activated])`;

      document.querySelectorAll(selector).forEach((oldScript) => {
        const script = document.createElement("script");
        const src = oldScript.getAttribute("data-src");

        if (src) {
          script.src = src;
          if (oldScript.hasAttribute("data-async")) {
            script.async = true;
          }
        } else {
          script.textContent = oldScript.textContent || "";
        }

        if (oldScript.id) {
          script.id = oldScript.id;
        }

        oldScript.setAttribute("data-activated", "true");
        oldScript.replaceWith(script);
      });
    },
  };
}
